<template>
  <b-modal
    id="ship-all-modal"
    size="xl"
    centered
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    title="Ship All"
    @show="onShow"
    @hidden="onHidden"
  >
    <template #modal-header>
      <div class="w-100">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h5 class="mb-0">
            Ship All
          </h5>
          <b-button
            variant="flat-secondary"
            size="sm"
            @click="onCloseClick"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
        <div class="d-flex">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="flex-fill text-center"
          >
            <div
              class="step-indicator"
              :class="{
                'step-active': currentStep === index + 1,
                'step-completed': currentStep > index + 1,
                'step-pending': currentStep < index + 1,
              }"
            >
              <small>{{ step }}</small>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div
      v-if="isCheckingActive"
      class="text-center py-5"
    >
      <b-spinner />
      <p class="text-muted mt-1">
        Checking for active shipments...
      </p>
    </div>

    <collection-step
      v-if="currentStep === 1 && !isCheckingActive"
      :selected-collection="selectedCollection"
      @select="onSelectCollection"
    />

    <product-step
      v-if="currentStep === 2 && !isCheckingActive"
      :collection-id="selectedCollectionId"
      :collection-name="selectedCollection"
      :selected-product-ids="selectedProductIds"
      @update="onUpdateProducts"
    />

    <order-step
      v-if="currentStep === 3 && !isCheckingActive"
      :collection-id="selectedCollectionId"
      :product-ids="selectedProductIds"
      :selected-order-ids="selectedOrderIds"
      @update="onUpdateOrders"
    />

    <processing-step
      v-if="currentStep === 4 && !isCheckingActive"
      :batch-id="batchId"
      :batch-status="batchStatus"
      :triggered-by="triggeredBy"
      @status-update="onBatchStatusUpdate"
      @retry="onRetry"
    />

    <template #modal-footer>
      <div
        v-if="currentStep < 4"
        class="w-100 d-flex justify-content-between"
      >
        <b-button
          variant="outline-secondary"
          :disabled="currentStep === 1"
          @click="prevStep"
        >
          Back
        </b-button>
        <b-button
          v-if="currentStep < 3"
          variant="primary"
          :disabled="!canProceed"
          @click="nextStep"
        >
          Next
        </b-button>
        <b-button
          v-if="currentStep === 3"
          variant="primary"
          :disabled="selectedOrderIds.length === 0 || isSubmitting"
          @click="onShipAll"
        >
          <b-spinner
            v-if="isSubmitting"
            small
            class="mr-50"
          />
          Create Shipment ({{ selectedOrderIds.length }})
        </b-button>
      </div>
      <div
        v-else
        class="w-100 d-flex justify-content-end"
      >
        <b-button
          variant="primary"
          :disabled="batchStatus === 'processing'"
          @click="onClose"
        >
          Done
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import store from '@/store'
import { CREATE_BULK_SHIPMENT, GET_ACTIVE_BULK_SHIPMENT } from '@/store/modules/shipment.module'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import CollectionStep from './steps/CollectionStep.vue'
import ProductStep from './steps/ProductStep.vue'
import OrderStep from './steps/OrderStep.vue'
import ProcessingStep from './steps/ProcessingStep.vue'

export default {
  components: {
    BModal,
    BButton,
    BSpinner,
    FeatherIcon,
    CollectionStep,
    ProductStep,
    OrderStep,
    ProcessingStep,
  },
  data() {
    return {
      currentStep: 1,
      steps: ['Collection', 'Products', 'Orders', 'Processing'],
      selectedCollection: '',
      selectedCollectionId: '',
      selectedProductIds: [],
      selectedOrderIds: [],
      batchId: null,
      batchStatus: null,
      triggeredBy: '',
      isSubmitting: false,
      isCheckingActive: false,
      hasNavigationGuard: false,
    }
  },
  computed: {
    canProceed() {
      if (this.currentStep === 1) return !!this.selectedCollectionId
      if (this.currentStep === 2) return this.selectedProductIds.length > 0
      return true
    },
  },
  methods: {
    async onShow() {
      this.resetState()
      this.addNavigationGuard()
      this.isCheckingActive = true
      try {
        const res = await store.dispatch(GET_ACTIVE_BULK_SHIPMENT)
        const batch = res?.data?.data?.batch
        if (batch) {
          this.batchId = batch.batchId
          this.batchStatus = batch.status
          this.triggeredBy = batch.triggeredBy || ''
          this.currentStep = 4
        }
      } catch {
        // If check fails, just start fresh
      } finally {
        this.isCheckingActive = false
      }
    },
    onHidden() {
      this.removeNavigationGuard()
      this.$emit('closed')
    },
    resetState() {
      this.currentStep = 1
      this.selectedCollection = ''
      this.selectedCollectionId = ''
      this.selectedProductIds = []
      this.selectedOrderIds = []
      this.batchId = null
      this.batchStatus = null
      this.triggeredBy = ''
      this.isSubmitting = false
    },
    nextStep() {
      if (this.currentStep < 4) {
        this.currentStep++
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },
    onSelectCollection({ id, name }) {
      if (this.selectedCollectionId !== id) {
        this.selectedCollectionId = id
        this.selectedCollection = name
        this.selectedProductIds = []
        this.selectedOrderIds = []
      }
    },
    onUpdateProducts(productIds) {
      this.selectedProductIds = productIds
      // Reset orders when products change
      this.selectedOrderIds = []
    },
    onUpdateOrders(orderIds) {
      this.selectedOrderIds = orderIds
    },
    async onShipAll() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `You are about to create shipments for ${this.selectedOrderIds.length} orders. This action cannot be undone. Do you want to continue?`,
        {
          title: 'Confirm Bulk Shipment',
          okVariant: 'primary',
          okTitle: 'Yes, Ship All',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (!confirmed) return

      this.isSubmitting = true
      try {
        const res = await store.dispatch(CREATE_BULK_SHIPMENT, {
          collectionId: this.selectedCollectionId,
          productIds: this.selectedProductIds,
          orderIds: this.selectedOrderIds,
        })
        this.batchId = res.data.data.batchId
        this.batchStatus = 'processing'
        this.currentStep = 4
      } catch (err) {
        apiToastError(err)
      } finally {
        this.isSubmitting = false
      }
    },
    onBatchStatusUpdate(status) {
      this.batchStatus = status.status
      if (status.triggeredBy) this.triggeredBy = status.triggeredBy
      if (status.status === 'completed') {
        const {
 succeeded, failed, skipped, totalOrders,
} = status
        apiToastSuccess(`${succeeded} of ${totalOrders} shipments created${failed > 0 ? `, ${failed} failed` : ''}${skipped > 0 ? `, ${skipped} skipped` : ''}`)
      }
    },
    onRetry() {
      this.batchStatus = 'processing'
    },
    onCloseClick() {
      if (this.currentStep > 1 && this.currentStep < 4) {
        this.$bvModal.msgBoxConfirm('Are you sure you want to close? Your selections will be lost.', {
          title: 'Confirm',
          okVariant: 'danger',
          okTitle: 'Yes, close',
          cancelTitle: 'Cancel',
          centered: true,
        }).then(confirmed => {
          if (confirmed) this.onClose()
        })
      } else {
        this.onClose()
      }
    },
    onClose() {
      this.removeNavigationGuard()
      this.$bvModal.hide('ship-all-modal')
    },
    addNavigationGuard() {
      if (this.hasNavigationGuard) return
      this.navigationGuard = this.$router.beforeEach((to, from, next) => {
        if (this.currentStep > 1 && this.currentStep < 4) {
          this.$bvModal.msgBoxConfirm('Are you sure you want to leave? Your Ship All progress will be lost.', {
            title: 'Confirm Navigation',
            okVariant: 'danger',
            okTitle: 'Yes, leave',
            cancelTitle: 'Stay',
            centered: true,
          }).then(confirmed => {
            if (confirmed) {
              this.onClose()
              next()
            } else {
              next(false)
            }
          })
        } else {
          this.onClose()
          next()
        }
      })
      this.hasNavigationGuard = true
    },
    removeNavigationGuard() {
      if (this.navigationGuard) {
        this.navigationGuard()
        this.navigationGuard = null
        this.hasNavigationGuard = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.step-indicator {
  padding: 6px 12px;
  border-bottom: 2px solid transparent;

  &.step-active {
    border-bottom-color: #7367f0;
    color: #7367f0;
    font-weight: 600;
  }

  &.step-completed {
    border-bottom-color: #28c76f;
    color: #28c76f;
  }

  &.step-pending {
    color: #b4b7bd;
  }
}
</style>
